import { hasPermission, type Permission, type Principal } from "@island/auth-core";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { createApiClient, type ApiClient } from "@island/browser-client";

/**
 * Who is signed in, and what they may do.
 *
 * Permissions come from the server on every session bootstrap and are used **only to
 * decide what to render**. Every route enforces them independently — a dashboard
 * that hid a button would still be talking to an API that refuses the request, which
 * is the arrangement that matters. Hiding it is a courtesy, not a control.
 */

interface SessionState {
  readonly principal: Principal | null;
  readonly permissions: readonly Permission[];
  readonly status: "loading" | "authenticated" | "anonymous";
}

interface SessionValue extends SessionState {
  readonly api: ApiClient;
  readonly can: (permission: Permission) => boolean;
  readonly signIn: (email: string, password: string, rememberMe: boolean) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

interface MeResponse {
  readonly principal: Principal;
  readonly permissions: readonly Permission[];
}

export function SessionProvider({
  baseUrl,
  children,
}: {
  readonly baseUrl: string;
  readonly children: ReactNode;
}) {
  const [state, setState] = useState<SessionState>({
    principal: null,
    permissions: [],
    status: "loading",
  });

  const api = useMemo(
    () =>
      createApiClient({
        baseUrl,
        // Staff, always. An agent who also shops on the site holds a second session
        // in the same cookie jar, and this is what keeps the dashboard reading its
        // own rather than whichever was written most recently.
        audience: "staff",
        // The client cannot recover the session itself, so it tells the shell, which
        // returns the whole app to the login screen in one place.
        onSessionLost: () => {
          setState({ principal: null, permissions: [], status: "anonymous" });
        },
      }),
    [baseUrl],
  );

  const loadSession = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>("/auth/me");

      setState({
        principal: me.principal,
        permissions: me.permissions,
        status: "authenticated",
      });
    } catch {
      // Any failure here means "not signed in". The cookie may be absent, expired,
      // or revoked, and the dashboard's response is identical in every case.
      setState({ principal: null, permissions: [], status: "anonymous" });
    }
  }, [api]);

  useEffect(() => {
    void loadSession();
  }, [loadSession]);

  const signIn = useCallback(
    async (email: string, password: string, rememberMe: boolean) => {
      await api.post("/auth/agent/login", { email, password, rememberMe });
      // Re-read rather than trusting the login response: the session is whatever the
      // server says it is, and `/auth/me` is the one place that answer comes from.
      await loadSession();
    },
    [api, loadSession],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      // Cleared even if the call failed. A logout that leaves the UI signed in
      // because the network blipped is worse than one that is optimistic.
      setState({ principal: null, permissions: [], status: "anonymous" });
    }
  }, [api]);

  const value = useMemo<SessionValue>(
    () => ({
      ...state,
      api,
      can: (permission) => state.principal !== null && hasPermission(state.principal, permission),
      signIn,
      signOut,
    }),
    [state, api, signIn, signOut],
  );

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionValue {
  const value = use(SessionContext);

  if (value === null) {
    throw new Error("useSession must be used inside a SessionProvider.");
  }

  return value;
}

/** The signed-in agent, or null when the principal is not staff. */
export function useAgent(): Extract<Principal, { type: "agent" }> | null {
  const { principal } = useSession();

  return principal?.type === "agent" ? principal : null;
}
